import { useEffect, useState } from "react";
import { webinarRepository } from "../../webinars/repository";
import { isAdminError } from "../../webinars/types";
import { useCooldown } from "../../hooks/useCooldown";
import visibilityOn from "../../assets/images/icons/visibility_60dp_024498_FILL1_wght400_GRAD0_opsz48.png";
import visibilityOff from "../../assets/images/icons/visibility_off_60dp_00000_FILL1_wght400_GRAD0_opsz48.png";

// Supabase Auth sign-in: email and password. The database separately checks that the account is
// listed as an administrator before showing anything.
//
// Repeated wrong passwords pause the form here: four tries, then 1 minute, doubling up to 15.
// The pause lives in this browser; on the server, Supabase Auth limits attempts per IP address.

const FREE_ATTEMPTS = 4;
const STORAGE_KEY = "km-admin-signin";

/** Seconds to pause after this many failures in a row */
const pauseFor = (failures: number) =>
  failures <= FREE_ATTEMPTS ? 0 : Math.min(15 * 60, 60 * 2 ** (failures - FREE_ATTEMPTS - 1));

function loadFailures(): { failures: number; until: number } {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
    return { failures: Number(saved.failures) || 0, until: Number(saved.until) || 0 };
  } catch {
    return { failures: 0, until: 0 };
  }
}

function saveFailures(value: { failures: number; until: number } | null) {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked: the pause just won't survive a reload
  }
}

export default function AdminSignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [failures, setFailures] = useState(() => loadFailures().failures);
  const cooldown = useCooldown(loadFailures().until);

  // Once a pause ends, the message about it goes too
  useEffect(() => {
    if (!cooldown.active) setError(previous => (previous.startsWith("Too many attempts") ? "" : previous));
  }, [cooldown.active]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cooldown.active) return;
    setError("");
    setBusy(true);
    // Don't leave the password readable on screen once it's been sent
    setShowPassword(false);
    try {
      await webinarRepository.signIn(email, password);
      saveFailures(null);
    } catch (err) {
      const count = failures + 1;
      // Supabase Auth's own per-IP limit: wait at least 5 minutes
      const serverWait = isAdminError(err) && err.code === "rate_limited" ? Math.max(err.retryAfter ?? 0, 5 * 60) : 0;
      const wait = serverWait || pauseFor(count);
      const until = wait ? Date.now() + wait * 1000 : 0;
      setFailures(count);
      saveFailures({ failures: count, until });
      cooldown.start(until);
      setError(wait
        ? "Too many attempts. Please wait before trying again."
        : isAdminError(err) ? err.message : "Couldn't sign in. Check your connection and try again.");
      setPassword("");
      setBusy(false);
    }
  };

  return (
    <form className="wa-panel wa-signin" onSubmit={submit}>
      <h1>Administrator sign-in</h1>
      <p className="wa-muted">For Kole Medical staff who manage webinar registrations.</p>
      <label className="wa-field">
        Email address
        <input type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} required />
      </label>
      <div className="wa-field">
        <label htmlFor="admin-password">Password</label>
        <div className="wa-password">
          <input
            id="admin-password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
          {/* Keeps the cursor in the field when clicked with a mouse */}
          <button
            type="button"
            className="wa-password-toggle"
            aria-controls="admin-password"
            aria-pressed={showPassword}
            aria-label={showPassword ? "Hide password" : "Show password"}
            title={showPassword ? "Hide password" : "Show password"}
            onMouseDown={e => e.preventDefault()}
            onClick={() => setShowPassword(shown => !shown)}
          >
            <img src={showPassword ? visibilityOff : visibilityOn} alt="" />
          </button>
        </div>
      </div>
      {error && <p className="wa-form-error" role="alert">{error}</p>}
      <button type="submit" className="km-btn km-btn--primary km-btn--block" disabled={busy || cooldown.active}>
        {busy ? <><span className="km-spinner" aria-hidden="true" />Signing in…</>
          : cooldown.active ? `Try again in ${cooldown.label}`
          : "Sign in"}
      </button>
    </form>
  );
}
